<script lang="ts">
  import { onMount, tick } from 'svelte';
  import { type AppContext, WebGLUnavailableError, boot } from '../app';
  import { lang, t } from '../i18n';
  import { get } from 'svelte/store';
  import { reducedEffects } from '../state/settings';
  import { introActive, photoMode } from '../state/stores';
  import { yieldToMain } from '../scene/Scene';
  import { startUrlSync } from '../state/urlSync';
  import Hud from './Hud.svelte';
  import Intro from './Intro.svelte';
  import { warmFormatters } from './format';
  import { warmFonts } from './fonts';

  let canvas: HTMLCanvasElement;
  let labelLayer: HTMLDivElement;
  let ctx = $state<AppContext | null>(null);
  let error = $state<'webgl' | 'data' | null>(null);
  let progress = $state(0);

  // the in-app reduced-effects setting switches CSS motion off like the OS setting (S2-16)
  $effect(() => {
    document.documentElement.toggleAttribute('data-reduced-motion', $reducedEffects);
  });

  onMount(() => {
    let stopUrlSync = (): void => {};
    // while the data loads: otherwise the first frame or the HUD mount pays for the locale data (S4-03)
    setTimeout(() => void warmFormatters(get(lang)), 0);
    let fontsWarmed = false;
    boot(canvas, labelLayer, (f) => {
      progress = f;
      // once the data is in, before the HUD mounts: requested before the splash's paint, the fonts would count into
      // Lighthouse's simulated LCP (S4-03 part 6)
      if (f >= 0.7 && !fontsWarmed) {
        fontsWarmed = true;
        void warmFonts();
      }
    })
      // the HUD mounts in tasks of its own, then the scene starts (S4-03: no long task before the first frame)
      .then(async (c) => {
        await yieldToMain();
        performance.mark('zeitflug:hud');
        if (c.intro) introActive.set(true);
        ctx = c;
        await tick();
        performance.measure('zeitflug:hud-mount', 'zeitflug:hud');
        // from now on the address is a direct link to the view
        stopUrlSync = startUrlSync();
      })
      .catch((err: unknown) => {
        console.error(err);
        error = err instanceof WebGLUnavailableError ? 'webgl' : 'data';
      });
    return () => {
      stopUrlSync();
      ctx?.scene.dispose();
    };
  });
</script>

<canvas class="scene" bind:this={canvas} aria-label={$t('app.scene')}></canvas>
<div class="label-layer" class:hidden={$photoMode} class:intro={$introActive} bind:this={labelLayer}></div>

{#if ctx}
  <!-- the scene starts once the HUD is in: its first framing fits the free view (S4-03 part 6) -->
  <Hud {ctx} onstaged={() => ctx?.scene.start()} />
  {#if $introActive}
    <Intro {ctx} />
  {/if}
{:else}
  <div class="splash" role="status">
    <h1>Zeitflug</h1>
    <p class="tagline">{$t('app.tagline')}</p>
    {#if error === 'webgl'}
      <p class="error">{$t('app.errorWebgl')}</p>
    {:else if error === 'data'}
      <p class="error">{$t('app.errorData')}</p>
    {:else}
      <!-- loading bar in the style of the time-warp throttle (S2-12) -->
      <div
        class="progress"
        role="progressbar"
        aria-label={$t('app.progress')}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(progress * 100)}
      >
        {#each Array.from({ length: 10 }, (_, i) => i) as i (i)}
          <span class="seg" class:on={progress * 10 > i}></span>
        {/each}
      </div>
      <p class="loading">{$t('app.loading')}</p>
    {/if}
  </div>
{/if}

<style>
  .label-layer.hidden {
    visibility: hidden;
  }
  /* the labels fade in when the intro ends (during it the scene does not place them) */
  .label-layer {
    transition: opacity 0.8s var(--ease);
  }
  .label-layer.intro {
    opacity: 0;
  }
  @media (prefers-reduced-motion: reduce) {
    .label-layer {
      transition: none;
    }
  }
</style>
